import { ICONS } from "@azm/core/constants/assets";
import type { ISidebarItem } from "@azm/core/interfaces/sidebar-item";

function hasMenuItem(items: ISidebarItem[], match: (item: ISidebarItem) => boolean): boolean {
	for (const item of items) {
		if (match(item)) return true;
		if ("children" in item && item.children?.length) {
			for (const child of item.children) {
				if (match(child as ISidebarItem)) return true;
			}
		}
	}
	return false;
}

function insertAfterDashboard(items: ISidebarItem[], extra: ISidebarItem): ISidebarItem[] {
	const out = [...items];
	const dashboardIdx = out.findIndex((item) => item.code === "dashboard");
	if (dashboardIdx >= 0) {
		out.splice(dashboardIdx + 1, 0, extra);
	} else {
		out.push(extra);
	}
	return out;
}

function isNoticesItem(item: ISidebarItem): boolean {
	const link = "routerLinkName" in item ? item.routerLinkName : undefined;
	return link === "notices" || link === "notes" || item.code === "notices" || item.code === "notes";
}

function isProfessionalMonitoringItem(item: ISidebarItem): boolean {
	return "routerLinkName" in item && item.routerLinkName === "professional-monitoring";
}

export function mergeInternalNoticesMenuItem(items: ISidebarItem[], isInternal: boolean, label: string): ISidebarItem[] {
	if (!isInternal || hasMenuItem(items, isNoticesItem)) return items;
	return insertAfterDashboard(items, {
		code: "notices",
		label,
		icon: "notices",
		routerLinkName: "notices",
	});
}

export function mergeInternalProfessionalMonitoringMenuItem(
	items: ISidebarItem[],
	isInternal: boolean,
	label: string,
): ISidebarItem[] {
	if (!isInternal || hasMenuItem(items, isProfessionalMonitoringItem)) return items;
	return insertAfterDashboard(items, {
		code: "professional-monitoring",
		label,
		icon: "lawsuits",
		routerLinkName: "professional-monitoring",
	});
}

function isAuditLogItem(item: ISidebarItem): boolean {
	const link = "routerLinkName" in item ? item.routerLinkName : undefined;
	return item.code === "audit-log" || link === "audit-log" || link === "audit-log/transactions" || link === "audit-log/general";
}

function insertBeforeReports(items: ISidebarItem[], extra: ISidebarItem): ISidebarItem[] {
	const out = [...items];
	const reportsIdx = out.findIndex((item) => {
		const link = "routerLinkName" in item ? item.routerLinkName : undefined;
		return item.code === "reports" || link === "reports";
	});
	if (reportsIdx >= 0) {
		out.splice(reportsIdx, 0, extra);
		return out;
	}
	out.push(extra);
	return out;
}

export function mergeInternalAuditLogMenuItem(
	items: ISidebarItem[],
	isInternal: boolean,
	labels: { title: string; transactions: string; general: string },
): ISidebarItem[] {
	if (!isInternal || hasMenuItem(items, isAuditLogItem)) return items;
	return insertBeforeReports(items, {
		code: "audit-log",
		label: labels.title,
		icon: ICONS.history,
		children: [
			{
				code: "audit-log-transactions",
				parentCode: "audit-log",
				label: labels.transactions,
				routerLinkName: "audit-log/transactions",
			},
			{
				code: "audit-log-general",
				parentCode: "audit-log",
				label: labels.general,
				routerLinkName: "audit-log/general",
			},
		],
	});
}

function isConversationHistoryItem(item: ISidebarItem): boolean {
	const link = "routerLinkName" in item ? item.routerLinkName : undefined;
	return item.code === "conversation-history" || link === "conversation-history";
}

export function mergeInternalConversationHistoryMenuItem(items: ISidebarItem[], isInternal: boolean, label: string): ISidebarItem[] {
	if (!isInternal || hasMenuItem(items, isConversationHistoryItem)) return items;
	return insertBeforeReports(items, {
		code: "conversation-history",
		label,
		icon: ICONS.chatbot,
		routerLinkName: "conversation-history",
	});
}

export function mergeInternalSidebarItems(
	items: ISidebarItem[],
	isInternal: boolean,
	noticesLabel: string,
	pmLabel: string,
	auditLogLabels: { title: string; transactions: string; general: string },
	conversationHistoryLabel: string,
): ISidebarItem[] {
	return mergeInternalConversationHistoryMenuItem(
		mergeInternalAuditLogMenuItem(
			mergeInternalProfessionalMonitoringMenuItem(
				mergeInternalNoticesMenuItem(items, isInternal, noticesLabel),
				isInternal,
				pmLabel,
			),
			isInternal,
			auditLogLabels,
		),
		isInternal,
		conversationHistoryLabel,
	);
}
